import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Code,
  Award,
  Calendar,
  BarChart3,
  Users,
  Loader2,
  Copy,
  ExternalLink,
  Globe,
  Tag,
  Layers
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEmbedsPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [organisers, setOrganisers] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [badgeStyle, setBadgeStyle] = useState('standard');
  const [embedCode, setEmbedCode] = useState('');
  const [generatingBadge, setGeneratingBadge] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, logsRes, organisersRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/embeds/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/embeds/logs?limit=20`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/embeds/organisers`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/events`, { headers })
      ]);
      
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setOrganisers(organisersRes.data.organisers || []);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      toast.error('Failed to load embed data');
    } finally {
      setLoading(false);
    }
  };

  const generateBadge = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    
    try {
      setGeneratingBadge(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/public/embed/badge/${selectedEvent}?style=${badgeStyle}`
      );
      setEmbedCode(response.data.embedCode);
      toast.success('Badge code generated!');
    } catch (error) {
      toast.error('Failed to generate badge');
    } finally {
      setGeneratingBadge(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin/events" className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-2">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Admin
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Code className="w-8 h-8 mr-3 text-orange-600" />
              Embed & Backlinks
            </h1>
            <p className="text-gray-500 mt-1">Generate embeddable badges and track backlink usage</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Accesses</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.totalAccesses || 0}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Badge Embeds</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.totalByType?.badge || 0}</p>
                  </div>
                  <Award className="w-8 h-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Calendar Widgets</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.totalByType?.calendar_widget || 0}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unique Organisers</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.uniqueOrganisers || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="generator" className="space-y-6">
            <TabsList>
              <TabsTrigger value="generator">Badge Generator</TabsTrigger>
              <TabsTrigger value="feeds">Calendar Feeds</TabsTrigger>
              <TabsTrigger value="logs">Access Logs</TabsTrigger>
              <TabsTrigger value="organisers">Organisers</TabsTrigger>
            </TabsList>

            {/* Badge Generator Tab */}
            <TabsContent value="generator">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="w-5 h-5 mr-2 text-purple-600" />
                    "As Featured On Jigsaw" Badge Generator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Event</label>
                        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose an event..." />
                          </SelectTrigger>
                          <SelectContent>
                            {events.map(event => (
                              <SelectItem key={event.id} value={event.id}>
                                {event.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Badge Style</label>
                        <Select value={badgeStyle} onValueChange={setBadgeStyle}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard (Full with event name)</SelectItem>
                            <SelectItem value="compact">Compact (One line)</SelectItem>
                            <SelectItem value="minimal">Minimal (Text only)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button 
                        onClick={generateBadge} 
                        disabled={generatingBadge || !selectedEvent}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {generatingBadge ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Code className="w-4 h-4 mr-2" />
                        )}
                        Generate Badge Code
                      </Button>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                      <div className="bg-gray-100 p-8 rounded-lg min-h-[150px] flex items-center justify-center">
                        {embedCode ? (
                          <div dangerouslySetInnerHTML={{ __html: embedCode }} />
                        ) : (
                          <p className="text-gray-400">Select an event and generate badge to preview</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {embedCode && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Embed Code</label>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(embedCode)}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <textarea
                        readOnly
                        value={embedCode}
                        className="w-full h-32 p-3 font-mono text-xs bg-gray-900 text-green-400 rounded-lg"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Calendar Feeds Tab */}
            <TabsContent value="feeds">
              <div className="grid gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Globe className="w-5 h-5 mr-2 text-blue-600" />
                      Organiser Calendar Feeds
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Organisers can embed a calendar widget showing all their events. The embed URL is:
                    </p>
                    <code className="block bg-gray-100 p-3 rounded text-sm mb-4">
                      /api/public/embed/organiser-feed/&#123;organiser-email&#125;
                    </code>
                    <p className="text-sm text-gray-500">
                      Returns iframe and JavaScript embed options with UTM tracking.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Tag className="w-5 h-5 mr-2 text-purple-600" />
                      Sector Calendar Feeds
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Partners can embed sector-specific event feeds:
                    </p>
                    <code className="block bg-gray-100 p-3 rounded text-sm mb-4">
                      /api/public/embed/sector-feed/&#123;sector-slug&#125;
                    </code>
                    <div className="flex flex-wrap gap-2">
                      {['technology', 'healthcare', 'events-industry', 'retail'].map(sector => (
                        <Badge key={sector} variant="outline" className="cursor-pointer">
                          {sector}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Layers className="w-5 h-5 mr-2 text-teal-600" />
                      Series Calendar Feeds
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Embed all editions of an event series:
                    </p>
                    <code className="block bg-gray-100 p-3 rounded text-sm">
                      /api/public/embed/series-feed/&#123;series-key&#125;
                    </code>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Access Logs Tab */}
            <TabsContent value="logs">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Embed Accesses</CardTitle>
                </CardHeader>
                <CardContent>
                  {logs.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No embed accesses recorded yet</p>
                  ) : (
                    <div className="space-y-3">
                      {logs.map((log, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {log.type === 'badge' ? (
                              <Award className="w-5 h-5 text-purple-500" />
                            ) : log.type === 'calendar_widget' ? (
                              <Calendar className="w-5 h-5 text-blue-500" />
                            ) : (
                              <Globe className="w-5 h-5 text-green-500" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {log.eventName || log.organiserName || 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {log.type} • {new Date(log.accessedAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Badge variant="outline">{log.type}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Organisers Tab */}
            <TabsContent value="organisers">
              <Card>
                <CardHeader>
                  <CardTitle>Organisers Using Embeds</CardTitle>
                </CardHeader>
                <CardContent>
                  {organisers.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No organisers have accessed embed codes yet</p>
                  ) : (
                    <div className="space-y-3">
                      {organisers.map((org, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">
                              {org.organiserName || org._id}
                            </p>
                            <p className="text-sm text-gray-500">{org._id}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">{org.accessCount} accesses</p>
                            <p className="text-xs text-gray-500">
                              Last: {new Date(org.lastAccessed).toLocaleDateString()}
                            </p>
                            <div className="flex gap-1 mt-1">
                              {org.types?.map((type, j) => (
                                <Badge key={j} variant="secondary" className="text-xs">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
